import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { courseService } from '../services/courseService';
import { Course } from '../types/course';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { 
  Play, 
  Clock, 
  CheckCircle, 
  BookOpen, 
  Users, 
  BarChart3, 
  Calendar,
  Star,
  Award,
  TrendingUp,
  Filter,
  Search,
  ChevronRight,
  Zap,
  Target,
  BookOpen as BookOpenIcon,
  Sparkles,
  Shield,
  Eye
} from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const { translate } = useLanguage();
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');

  useEffect(() => {
    const allCourses = courseService.getCourses();
    setCourses(allCourses);
    setFilteredCourses(allCourses);
  }, []);

  useEffect(() => {
    let filtered = courses;

    // Apply search filter
    if (searchQuery) {
      filtered = courseService.searchCourses(searchQuery);
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    // Apply level filter
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(course => course.level === selectedLevel);
    }

    setFilteredCourses(filtered);
  }, [searchQuery, selectedCategory, selectedLevel, courses]);

  const getProgressData = () => {
    if (!user) return { completedHours: 0, totalRequired: 35, progressPercentage: 0 };

    const requiredCourses = courseService.getRequiredCourses();
    let totalCompleted = 0;
    let totalRequired = 0;

    requiredCourses.forEach(course => {
      const progress = courseService.getUserProgress(user.id, course.id);
      totalRequired += course.requiredHours;
      if (progress) {
        const completionPercentage = courseService.calculateCompletionPercentage(user.id, course.id);
        totalCompleted += (course.requiredHours * completionPercentage) / 100;
      }
    });

    return {
      completedHours: Math.round(totalCompleted * 10) / 10,
      totalRequired,
      progressPercentage: Math.round((totalCompleted / totalRequired) * 100)
    };
  };

  const getRecommendedCourses = () => {
    if (!user) return [];
    return courseService.getRecommendedCourses(user.id);
  };

  const getCourseProgress = (courseId: string) => {
    if (!user) return 0;
    return courseService.calculateCompletionPercentage(user.id, courseId);
  };

  const getCourseStatus = (course: Course) => {
    if (!user) return 'available';
    const progress = courseService.getUserProgress(user.id, course.id);
    if (!progress) return 'available';
    
    const completionPercentage = courseService.calculateCompletionPercentage(user.id, course.id);
    if (completionPercentage === 100) return 'completed';
    if (completionPercentage > 0) return 'in-progress';
    return 'available';
  };

  const canAccessCourse = (courseId: string) => {
    if (!user) return false;
    return courseService.canAccessCourse(user.id, courseId);
  };

  const progressData = getProgressData();
  const recommendedCourses = getRecommendedCourses();

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'cpc', label: 'CPC Training' },
    { value: 'hazard', label: 'Hazard Perception' },
    { value: 'safety', label: 'Safety Training' },
    { value: 'regulations', label: 'Regulations' },
    { value: 'practical', label: 'Practical Skills' }
  ];

  const levels = [
    { value: 'all', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const getRoleWelcomeMessage = () => {
    switch (user?.role) {
      case 'admin':
        return {
          title: `Welcome back, ${user?.name}!`,
          subtitle: 'Manage your organization\'s training program and monitor compliance',
          icon: Shield,
          color: 'from-red-600 to-red-700'
        };
      case 'manager':
        return {
          title: `Welcome back, ${user?.name}!`,
          subtitle: 'Oversee team training progress and ensure regulatory compliance',
          icon: BarChart3,
          color: 'from-orange-600 to-orange-700'
        };
      case 'trainer':
        return {
          title: `Welcome back, ${user?.name}!`,
          subtitle: 'Create engaging courses and track learner progress',
          icon: BookOpen,
          color: 'from-purple-600 to-purple-700'
        };
      default:
        return {
          title: `Welcome back, ${user?.name}!`,
          subtitle: 'Ready to continue your professional development journey?',
          icon: Sparkles,
          color: 'from-blue-600 to-indigo-700'
        };
    }
  };

  const welcomeInfo = getRoleWelcomeMessage();
  const WelcomeIcon = welcomeInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className={`bg-gradient-to-r ${welcomeInfo.color} rounded-3xl p-8 text-white relative overflow-hidden`}>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24" />
            <div className="relative z-10">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                  <WelcomeIcon className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">{welcomeInfo.title}</h1>
                  <p className="text-blue-100">{welcomeInfo.subtitle}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center space-x-3">
                    <Target className="w-8 h-8 text-blue-200" />
                    <div>
                      <div className="text-2xl font-bold">{progressData.progressPercentage}%</div>
                      <div className="text-blue-200 text-sm">
                        {user?.role === 'driver' ? 'Overall Progress' : 'System Progress'}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center space-x-3">
                    <Clock className="w-8 h-8 text-blue-200" />
                    <div>
                      <div className="text-2xl font-bold">{progressData.completedHours}h</div>
                      <div className="text-blue-200 text-sm">
                        {user?.role === 'driver' ? 'Hours Completed' : 'Total Training Hours'}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center space-x-3">
                    <Award className="w-8 h-8 text-blue-200" />
                    <div>
                      <div className="text-2xl font-bold">
                        {courses.filter(c => getCourseStatus(c) === 'completed').length}
                      </div>
                      <div className="text-blue-200 text-sm">
                        {user?.role === 'driver' ? 'Certificates' : 'Available Courses'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Role-specific Quick Actions */}
        {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'trainer') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Quick Actions</h2>
                    <p className="text-gray-600 text-sm">Access your management tools</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link to="/admin">
                    <Button
                      variant="outline"
                      fullWidth
                      icon={<Shield className="w-4 h-4" />}
                    >
                      {user?.role === 'trainer' ? 'Training Management' : 'Admin Dashboard'}
                    </Button>
                  </Link>
                  
                  {(user?.role === 'admin' || user?.role === 'manager') && (
                    <Link to="/admin/users">
                      <Button
                        variant="outline"
                        fullWidth
                        icon={<Users className="w-4 h-4" />}
                      >
                        Manage Users
                      </Button>
                    </Link>
                  )}
                  
                  <Link to="/admin/courses/builder">
                    <Button
                      variant="outline"
                      fullWidth
                      icon={<BookOpen className="w-4 h-4" />}
                    >
                      {user?.role === 'trainer' ? 'Create Course' : 'Course Builder'}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Recommended Courses */}
        {recommendedCourses.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Star className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {user?.role === 'driver' ? 'Recommended for You' : 'Featured Training'}
                    </h2>
                    <p className="text-gray-600 text-sm">
                      {user?.role === 'driver' ? 'Continue your learning journey' : 'Popular courses in your organization'}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {recommendedCourses.slice(0, 2).map((course, index) => {
                    const progress = getCourseProgress(course.id);
                    const status = getCourseStatus(course);
                    
                    return (
                      <motion.div
                        key={course.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="relative"
                      >
                        <Card hover className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Badge variant="primary" size="sm">
                                    {course.category.toUpperCase()}
                                  </Badge>
                                  {course.isRequired && (
                                    <Badge variant="danger" size="sm">Required</Badge>
                                  )}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{course.title}</h3>
                                <p className="text-gray-600 text-sm mb-3">{course.description}</p>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <span className="flex items-center">
                                    <Clock className="w-4 h-4 mr-1" />
                                    {course.requiredHours}h
                                  </span>
                                  <span className="flex items-center">
                                    <BookOpen className="w-4 h-4 mr-1" />
                                    {course.modules.length} modules
                                  </span>
                                </div>
                              </div>
                              {status === 'completed' && (
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                </div>
                              )}
                            </div>

                            {status !== 'completed' && progress > 0 && (
                              <div className="mb-4">
                                <div className="flex justify-between text-sm text-gray-600 mb-1">
                                  <span>Progress</span>
                                  <span>{progress}%</span>
                                </div>
                                <div className="bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            <Link to={`/training/${course.id}`}>
                              <Button
                                variant="primary"
                                fullWidth
                                icon={<Play className="w-4 h-4" />}
                                iconPosition="left"
                              >
                                {progress > 0 ? 'Continue' : 'Start'} Training
                              </Button>
                            </Link>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Course Library */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Training Library</h2>
                  <p className="text-gray-600 mt-1">Explore all available courses and training modules</p>
                </div>
              
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                  <Input
                    placeholder="Search courses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    icon={<Search className="w-4 h-4" />}
                  />
                
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                
                  <select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {levels.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </CardHeader>
          
            <CardContent>
              {filteredCourses.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
                  <p className="text-gray-600">Try adjusting your search or filter criteria</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredCourses.map((course, index) => {
                    const progress = getCourseProgress(course.id);
                    const status = getCourseStatus(course);
                    const accessible = canAccessCourse(course.id);
                    
                    return (
                      <motion.div
                        key={course.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 + index * 0.05 }}
                      >
                        <Card 
                          hover={accessible}
                          className={!accessible ? 'bg-gray-50' : ''}
                        >
                          <CardContent className="p-6">
                            {/* Course Header */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Badge 
                                    variant={
                                      course.category === 'cpc' ? 'primary' :
                                      course.category === 'hazard' ? 'warning' :
                                      course.category === 'safety' ? 'success' : 'default'
                                    }
                                    size="sm"
                                  >
                                    {course.category.toUpperCase()}
                                  </Badge>
                                  <Badge 
                                    variant={
                                      course.level === 'beginner' ? 'success' :
                                      course.level === 'intermediate' ? 'warning' : 'danger'
                                    }
                                    size="sm"
                                  >
                                    {course.level}
                                  </Badge>
                                  {course.isRequired && (
                                    <Badge variant="danger" size="sm">Required</Badge>
                                  )}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{course.title}</h3>
                                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{course.description}</p>
                              </div>
                              {status === 'completed' && (
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                </div>
                              )}
                            </div>

                            {/* Course Stats */}
                            <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {course.requiredHours}h
                              </span>
                              <span className="flex items-center">
                                <BookOpen className="w-4 h-4 mr-1" />
                                {course.modules.length} modules
                              </span>
                              {course.certification && (
                                <span className="flex items-center">
                                  <Award className="w-4 h-4 mr-1" />
                                  Certificate
                                </span>
                              )}
                            </div>

                            {/* Progress Bar */}
                            {accessible && status !== 'completed' && progress > 0 && (
                              <div className="mb-4">
                                <div className="flex justify-between text-sm text-gray-600 mb-1">
                                  <span>Progress</span>
                                  <span>{progress}%</span>
                                </div>
                                <div className="bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Prerequisites Warning */}
                            {!accessible && course.prerequisites && (
                              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                                <p className="text-sm text-yellow-800">
                                  <strong>Prerequisites required:</strong> Complete prerequisite courses to unlock this training.
                                </p>
                              </div>
                            )}

                            {/* Action Button */}
                            {accessible ? (
                              <Link
                                to={`/training/${course.id}`}
                              >
                                <Button
                                  variant="primary"
                                  fullWidth
                                  icon={<Play className="w-4 h-4" />}
                                >
                                  {progress > 0 ? 'Continue' : 'Start'} Training
                                </Button>
                              </Link>
                            ) : (
                              <Button variant="outline" fullWidth disabled>
                                Prerequisites Required
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}